/**
 * Validation Expression Input Component
 * Provides an easy-to-use interface for writing validation expressions
 */

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
} from '@mui/material';

interface ValidationExpressionInputProps {
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
}

export const ValidationExpressionInput: React.FC<ValidationExpressionInputProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [textValue, setTextValue] = useState<string>('');

  // Initialize from prop
  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        // Legacy: plain string (shouldn't happen with new format)
        setTextValue(value);
      } else if (value.script) {
        // New format: { script: "...", errorMessage?: "..." }
        // Show just the script for editing, or full JSON if errorMessage exists
        if (value.errorMessage) {
          setTextValue(JSON.stringify(value, null, 2));
        } else {
          setTextValue(value.script);
        }
      } else {
        // Unknown format, show as JSON
        setTextValue(JSON.stringify(value, null, 2));
      }
    } else {
      setTextValue('');
    }
  }, [value]);

  // Parse on blur
  const handleBlur = () => {
    const val = textValue.trim();
    if (!val) {
      onChange(null);
      return;
    }

    // Try to parse as JSON object with script/errorMessage
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === 'object' && parsed.script) {
        onChange(parsed);
      } else {
        // Parsed successfully but not in expected format, wrap it
        onChange({ script: val });
      }
    } catch {
      // Not JSON, wrap plain expression in object for JSON storage
      onChange({ script: val });
    }
  };

  return (
    <TextField
      fullWidth
      multiline
      rows={6}
      size="small"
      value={textValue}
      onChange={(e) => setTextValue(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder="value > 0 && value < 100"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        '& .MuiInputBase-root': {
          bgcolor: 'grey.50',
        },
        '& .MuiInputBase-root.Mui-disabled': {
          bgcolor: 'action.hover',
          '& .MuiInputBase-input': {
            color: 'text.primary',
            WebkitTextFillColor: 'unset',
          },
        },
      }}
    />
  );
};

